import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
// import ReCAPTCHA from 'react-google-recaptcha'; // Commented out for future implementation
import API_CONFIG from '../../config/api';

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // const [captchaToken, setCaptchaToken] = useState<string | null>(null); // Commented out for future implementation
  const navigate = useNavigate();
  const location = useLocation();
  // const recaptchaRef = useRef<any>(null); // Commented out for future implementation
  
  // Check if running on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  // Get the redirect path if user was redirected from a protected route
  const from = (location.state as { from?: string })?.from || '/admin/dashboard';
  
  // API base URL from configuration
  const API_URL = API_CONFIG.baseURL;

  // Check if admin is already logged in
  React.useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userRole = localStorage.getItem('userRole');
    const userData = localStorage.getItem('userData');
    
    if (token) {
      let isAdmin = userRole === 'admin';
      if (!isAdmin && userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          isAdmin = parsedUserData.userType === 'Admin';
        } catch (error) {

        }
      }
      
      if (isAdmin) {
        navigate(from, { replace: true });
      }
    }
  }, [navigate, from]);

  // Commented out for future implementation
  // const handleCaptchaChange = (token: string | null) => {
  //   setCaptchaToken(token);
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      // Commented out for future implementation
      // Execute reCAPTCHA v3 for production
      // let captchaTokenToSend = null;
      // if (!isLocalhost && recaptchaRef.current) {
      //   captchaTokenToSend = await recaptchaRef.current.executeAsync();
      // }
      let captchaTokenToSend = null; // No reCAPTCHA for now
      
      // Admin password login
      const response = await axios.post(`${API_URL}/users/login-admin`, {
        username: username,
        password: password,
        captchaToken: captchaTokenToSend
      });

      if (response.data.success) {
        const userData = response.data.data;
        
        // Check if user is admin
        if (userData.userType !== 'Admin') {
          setError('دسترسی غیر مجاز. فقط مدیران سیستم می‌توانند وارد شوند.');
          return;
        }
        
        // Store token and user data
        const token = response.data.token.startsWith('Bearer ') 
          ? response.data.token.substring(7) 
          : response.data.token;
          
        localStorage.setItem('authToken', token);
        localStorage.setItem('userData', JSON.stringify(userData));
        localStorage.setItem('userRole', 'admin');
        
        // Navigate to admin area
        navigate(from);
      } else {
        setError(response.data.message || 'خطا در ورود');
      }
    } catch (err: any) {

      if (err.response?.status === 401) {
        setError('نام کاربری یا کلمه عبور اشتباه است');
      } else if (err.response?.status === 403) {
        setError('دسترسی غیر مجاز. فقط مدیران سیستم می‌توانند وارد شوند.');
      } else {
        setError(err.response?.data?.message || 'خطا در اتصال به سرور');
      }
      // Reset captcha on error - Commented out for future implementation
      // if (recaptchaRef.current) {
      //   recaptchaRef.current.reset();
      //   setCaptchaToken(null);
      // }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src="/assets/img/logoRazi.png" alt="Razi Logo" className="w-16 h-16" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">ورود مدیریت</h1>
            <p className="text-gray-600">پنل مدیریت آزمایشگاه رازی رشت</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                نام کاربری
              </label>
              <input 
                id="username"
                placeholder="نام کاربری خود را وارد کنید" 
                autoComplete="username" 
                name="username" 
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)} 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                کلمه عبور
              </label>
              <input 
                id="password"
                placeholder="کلمه عبور خود را وارد کنید" 
                autoComplete="current-password" 
                name="password" 
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                required
              />
            </div>

            {/* reCAPTCHA - Commented out for future implementation */}
            {/* {!isLocalhost && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey="6Lc7rcYrAAAAAI1b9LCiEuVnQAOEBYJ_ymEnhnx9"
                  onChange={handleCaptchaChange}
                  theme="light"
                  size="invisible"
                />
              </div>
            )} */}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button 
              type="submit" 
              disabled={isLoading || !username || !password}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
                  در حال ورود...
                </div>
              ) : (
                'ورود به پنل مدیریت'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                بازگشت به صفحه اصلی
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              ورود به پنل مدیریت فقط برای کارکنان مجاز آزمایشگاه رازی رشت امکان‌پذیر است.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;